import { ClientFooter } from '@ecosystem/newron-client-lib';
import { listsV2, navigation } from '@ecosystem/newron-design-system';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router';

import { Logo } from '../../components/logo/Logo';
import { PolicyPage } from '../../components/policy/PolicyPage';

const { Table } = listsV2;

export const POLICY_DATE = 'May 2025';

enum ProcessingPurposesTableKeys {
  ProcessingPurposes = 'ProcessingPurposes',
  DataCategories = 'DataCategories',
  LegalBasis = 'LegalBasis',
  StoragePeriods = 'StoragePeriods',
}

enum CookiesTableKeys {
  Category = 'Category',
  SubGroup = 'SubGroup',
  Name = 'Name',
  FirstOrThird = 'FirstOrThird',
  Domain = 'Domain',
  LifeTime = 'Lifetime',
}

const tableCellDefaults: listsV2.CellConfig = {
  subComponentAlign: 'left',
  maxWidth: 280,
  minWidth: 220,
  hasRightBorder: true,
  hasBottomBorder: true,
};

const TableSubComponent = (data: (string | string[])[] | string) =>
  Array.isArray(data) ? (
    <div className="w-full block mr-2 text-sm overflow-clip">
      {data.map(item =>
        Array.isArray(item) ? (
          <ul key={item[0]} style={{ marginBottom: '0.5rem' }}>
            {item.map(subItem => (
              <li key={subItem}>{subItem}</li>
            ))}
          </ul>
        ) : (
          <p key={item} style={{ marginBottom: '0.5rem' }}>
            {item}
          </p>
        )
      )}
    </div>
  ) : (
    <div className="mr-2 text-sm" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {data}
    </div>
  );

function PrivacyPolicy(): JSX.Element {
  return (
    <PolicyPage>
      <PolicyPage.Logo>
        <Logo />
      </PolicyPage.Logo>
      <PolicyPage.Title>Privacy Notice for gfknewron</PolicyPage.Title>
      <PolicyPage.Body>
        <PrivacyPolicyCopy />
        <PolicyPage.TOC>
          <PolicyPage.TOCLink id="Controller" label="Controller" />
          <PolicyPage.TOCLink id="content" label="Content" />
          <PolicyPage.TOCLink
            id="Personal data subjects and data categories"
            label="Personal data subjects and data categories"
          />
          <PolicyPage.TOCLink
            id="processing-purposes-legal-bases-data-storage"
            label="Processing purposes, legal bases of processing and data storage periods or rules"
          />
          <PolicyPage.TOCLink id="Publicly available sources" label="Publicly available sources" />
          <PolicyPage.TOCLink id="recipients" label="Recipients" />
          <PolicyPage.TOCLink id="Transfers of Data outside the EU/EEA" label="Transfers of Data outside the EU/EEA" />
          <PolicyPage.TOCLink id="obligation-to-provide-data" label="Are you obliged to provide your personal data?" />
          <PolicyPage.TOCLink id="Your data subject rights" label="Your data subject rights" />
          <PolicyPage.TOCLink id="duration-of-processing" label="Duration of the processing" />
          <PolicyPage.TOCLink id="security" label="Security" />
          <PolicyPage.TOCLink id="cookies-and-other-technologies" label="Cookies and other technologies" />
          <PolicyPage.TOCLink id="questions" label="Questions, exercising your data protection rights, complaints" />
        </PolicyPage.TOC>
      </PolicyPage.Body>
      <ClientFooter pageName="PrivacyPolicy" />
    </PolicyPage>
  );
}

function PersonalDataSubjectSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex w-full flex-col gap-2">
      <h3 className="text-md font-semibold w-1/4">{title}</h3>
      <div className="pl-4 w-3/4">{children}</div>
    </div>
  );
}

function PrivacyPolicyCopy(): JSX.Element {
  const { hash } = useLocation();

  const { Link } = navigation;
  useEffect(() => {
    setTimeout(() => {
      const { IsBannerLoaded } = (window as any)?.Optanon?.GetDomainData() ?? { IsBannerLoaded: false };

      if (IsBannerLoaded) {
        (window as any)?.Optanon?.initializeCookiePolicyHtml();
      }
    }, 100);
  }, []);

  useEffect(() => {
    if (hash) {
      const id = hash.substr(1).replace('%20', ' ');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView();
      }
    }
  }, [hash]);

  const openCookieSettings = () => {
    (window as any)?.Optanon?.ToggleInfoDisplay();
  };

  return (
    <PolicyPage.Content>
      <p>
        <Link href="https://platform.gfk.com/" external>
          https://platform.gfk.com/
        </Link>
      </p>
      <p>Last changed: {POLICY_DATE}</p>
      <p>
        This privacy notice informs you how GfK GmbH ("NIQ", "we", "us", "our") collects and processes your personal
        data in connection with our platform gfknewron ("Platform").
      </p>
      <p>
        NIQ is part of the NIQ group that consists of the companies listed{' '}
        <Link external href="https://nielseniq.com/global/en/legal/gdpr-operating-entities/">
          here
        </Link>
        , which together form the "Group".
      </p>
      <p>
        Where we refer to personal data below, we mean any information relating to an identified or identifiable living
        person. Personal data that has been anonymized in such a way that the data subject cannot be identified or can
        no longer be identified (anonymous data) is no longer considered personal data.
      </p>
      <p>
        We may need to amend or update this privacy notice from time to time. Therefore, please read this privacy notice
        at regular intervals.
      </p>
      <PolicyPage.Chapters>
        <PolicyPage.Chapter title="Controller, data protection officer" id="Controller">
          <p>For the purposes of this privacy notice, the controller is:</p>
          <h3>GfK GmbH</h3>
          <address>
            <p>
              Sophie-Germain-Strasse 3-5
              <br />
              90443 Nuremberg
              <br />
              Germany
              <br />
              <Link href="mailto:gfknewron@smb.nielseniq.com" external>
                gfknewron@smb.nielseniq.com
              </Link>
            </p>
          </address>
          <p>
            We have appointed a data protection officer that you can reach by e-mail:{' '}
            <Link href="mailto:NIQ@HewardMills.com" external>
              NIQ@HewardMills.com
            </Link>
          </p>
          <p>Or by post:</p>
          <address>
            <p>
              HewardMills
              <br />
              Ltd, 77 Farringdon Road,
              <br />
              London, EC1M 3JU,
              <br />
              United Kingdom
            </p>
          </address>
        </PolicyPage.Chapter>
        <PolicyPage.Chapter title="Content" id="content">
          <p>In this privacy notice for the Platform, we inform you about the following: </p>
          <ul>
            <li>Personal data subjects and data categories (Section III);</li>
            <li>Processing purposes, legal bases of processing and data storage periods or rules (Section IV);</li>
            <li>Personal data concerning you that we collect from other sources (Section V);</li>
            <li>Recipients (Section VI);</li>
            <li>Transfers of Data outside the EU/EEA (Section VII);</li>
            <li>Obligation to provide your personal data (Section VIII);</li>
            <li>Your data subject rights (Section IX);</li>
            <li>Duration of the processing (Section X);</li>
            <li>Security (Section XI);</li>
            <li>Cookies and similar technologies (Section XII);</li>
            <li>Questions, exercising your data protection rights, complaints (Section XIII).</li>
          </ul>
        </PolicyPage.Chapter>
        <PolicyPage.Chapter
          title="Personal data subjects and data categories"
          id="Personal data subjects and data categories"
        >
          <p>
            We process data of the below data categories Platform users ("Users"), Employees of the Group ("Employees")
            and prospective Users ("Prospective Users").
          </p>
          <PersonalDataSubjectSection title="User Account Data:">
            <ul>
              {[
                'Name',
                'Company name',
                'E-mail address',
                'Role (e.g. Manager)',
                'Career (e.g. Sales)',
                'Country',
                'Scope of responsibility (e.g. regional)',
                'Previous use of point-of-sale data (yes / no)',
                'Marketing consent',
                'Client status (trialist / paying subscriber)',
                'Client Platform subscription status (active / inactive)',
                'Client subscribed product(s)',
              ].map(item => (
                <li key={item} className="text-sm">
                  {item}
                </li>
              ))}
            </ul>
          </PersonalDataSubjectSection>
          <PersonalDataSubjectSection title="Access Log Data:">
            <ul>
              {[
                'Browser type and version',
                'Device operating system type and version',
                'IP address',
                'Device city and country location as inferred from IP address',
                '30-day history of access events',
              ].map(item => (
                <li key={item} className="text-sm">
                  {item}
                </li>
              ))}
            </ul>
          </PersonalDataSubjectSection>
          <PersonalDataSubjectSection title="Platform Usage Data:">
            <p>(A) Amplitude Analytics</p>
            <ul className="list-disc pl-4">
              {[
                'IP address',
                'Device manufacturer and model',
                'Device operating system type and version',
                'Date and time of log-in and log-out',
                'Login frequency, number of user logins over time',
                'Duration of visit on a subpage within the Platform',
                'SMTP-ID',
                'Browser type',
                'User interaction with the platform',
              ].map(item => (
                <li key={item} className="text-sm">
                  {item}
                </li>
              ))}
            </ul>
            <p>(B) Appcues</p>
            <ul className="list-disc pl-4">
              {[
                'User ID',
                'Subscription data',
                'Company',
                'Device type, OS, browser, browser language',
                'Platform current page URL, current page title',
                'Platform last page URL, last page title',
                'Page visited',
                'Link clicked',
                'Button clicked',
                'Session page views',
                'Timestamp of the event occurred',
              ].map(item => (
                <li key={item} className="text-sm">
                  {item}
                </li>
              ))}
            </ul>
            <p>(C) Hotjar</p>
            <ul className="list-disc pl-4">
              {[
                'IP address (stored in a de-identified format)',
                'Browser type',
                'Referrer URL and domain',
                'Preferred Platform language',
                'Device screen resolution',
                'Device manufacturer and model',
                'Device operating system type and version',
                'Device geographic (country) location',
                'Mouse events (movements, location, clicks)',
                'Keypresses',
                'Pages visited',
                'Date and time of accessed pages',
              ].map(item => (
                <li key={item} className="text-sm">
                  {item}
                </li>
              ))}
            </ul>
            <p>(D) Twilio SendGrid</p>
            <ul className="list-disc pl-4">
              {['Email address', 'First name'].map(item => (
                <li key={item} className="text-sm">
                  {item}
                </li>
              ))}
            </ul>
          </PersonalDataSubjectSection>
          <PersonalDataSubjectSection title="Prospective User Account Data">
            <ul>
              {[
                'Email address',
                'Client ID',
                'Platform permissions',
                'Date and time of invitation sent to the Prospective User',
                'Auth0ID of existing Platform User that initiated invitation to Platform',
              ].map(item => (
                <li key={item} className="text-sm">
                  {item}
                </li>
              ))}
            </ul>
          </PersonalDataSubjectSection>
        </PolicyPage.Chapter>
        <PolicyPage.Chapter
          title="Processing purposes, legal bases of processing and data storage periods or rules"
          id="processing-purposes-legal-bases-data-storage"
        >
          <Table
            columnConfig={[
              {
                columnKey: ProcessingPurposesTableKeys.ProcessingPurposes,
                columnName: 'Processing purposes',
                cellConfig: { ...tableCellDefaults, maxWidth: 180 },
                subComponent: TableSubComponent,
              },
              {
                columnKey: ProcessingPurposesTableKeys.DataCategories,
                columnName: 'Data categories',
                cellConfig: { ...tableCellDefaults, maxWidth: 180 },
                subComponent: TableSubComponent,
              },
              {
                columnKey: ProcessingPurposesTableKeys.LegalBasis,
                columnName: 'Legal basis of the processing',
                cellConfig: { ...tableCellDefaults, maxWidth: 200 },
                subComponent: TableSubComponent,
              },
              {
                columnKey: ProcessingPurposesTableKeys.StoragePeriods,
                columnName: 'Storage periods or rules',
                cellConfig: { ...tableCellDefaults, maxWidth: 100 },
                subComponent: TableSubComponent,
              },
            ]}
            isRowSelectable={false}
            data={[
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]: 'Running our authentication tool "Auth0"',
                [ProcessingPurposesTableKeys.DataCategories]: 'Access Log Data',
                [ProcessingPurposesTableKeys.LegalBasis]:
                  'Our legitimate interests pursuant to prevent and detect information security threats, personal data breaches and other disturbance or technical disruptions to the service provided through the Platform.',
                [ProcessingPurposesTableKeys.StoragePeriods]: '30 days',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]:
                  'Creating User accounts and enabling secure User log in',
                [ProcessingPurposesTableKeys.DataCategories]: 'Access Log Data, User Account Data',
                [ProcessingPurposesTableKeys.LegalBasis]:
                  'As applicable: Performance of our contract with clients for the provision of the Platform and our legitimate interests to provide the Platform to Employees.',
                [ProcessingPurposesTableKeys.StoragePeriods]: 'We delete your personal data upon request',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]:
                  'Creating User Data upon first log in. This includes manually adding information from other sources such as Salesforce where User Data is not provided by User upon first log-in.',
                [ProcessingPurposesTableKeys.DataCategories]: 'User Account Data',
                [ProcessingPurposesTableKeys.LegalBasis]:
                  'Our legitimate interests to better understand User’s needs and habits and to support User with the use of the Platform. ',
                [ProcessingPurposesTableKeys.StoragePeriods]: 'We delete your personal data upon request',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]:
                  'Creating Platform usage analytics via Amplitude Analytics to better understand usage habits, preferences and needs and to continuously improve the Platform, as well as to drive sales and renewals and to provide Users with tailored marketing communications. This may include creating User groups for Users which meet pre-defined criteria (e.g. client company) and transferring the data to Amplitude for analytics purposes.',
                [ProcessingPurposesTableKeys.DataCategories]: [
                  'User Account Data, Platform Usage Data (A) that includes the following user actions on the platform ("Events"):',
                  [
                    'Signed in',
                    'Signed out',
                    'Signed up',
                    'Navigation button clicked',
                    'Page viewed',
                    'Page scrolled',
                    'Page element viewed',
                    'Page element clicked',
                    'Filter applied',
                    'Table clicked',
                    'Chart clicked',
                    'Search clicked',
                    'Contact Us clicked',
                    'Email opened',
                    'Email delivered',
                    'Email link clicked',
                    'Email reported as Spam',
                  ],
                ],
                [ProcessingPurposesTableKeys.LegalBasis]: [
                  'Our legitimate interest pursuant to understand User experience and improve the Platform whereas User’s consent is our legal basis for any marketing communication we send to User.',
                  'User’s consent is our legal basis to the extent that we use Cookies (as defined below in Section XI) to collect Platform Usage Data.',
                ],
                [ProcessingPurposesTableKeys.StoragePeriods]:
                  'We delete your data upon request or anonymize it upon deletion of your account',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]:
                  'Creating Platform usage analytics via Amplitude Session replay to allow optimisation of the platform experience',
                [ProcessingPurposesTableKeys.DataCategories]: 'User Account Data, Platform Usage Data (A)',
                [ProcessingPurposesTableKeys.LegalBasis]: [
                  'Our legitimate interest to understand User experience and improve the Platform.',
                  'User’s consent is our legal basis to the extent that we use Cookies (as defined below in Section XI) to collect Platform Usage Data.',
                ],
                [ProcessingPurposesTableKeys.StoragePeriods]:
                  'We delete your data upon request or anonymize it upon deletion of your account',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]:
                  'Creating Platform usage analytics via Hotjar to better understand Users’ experience on the Platform so that we can optimize the Platform experience.',
                [ProcessingPurposesTableKeys.DataCategories]: 'User Account Data, Platform Usage Data (C)',
                [ProcessingPurposesTableKeys.LegalBasis]: [
                  'Our legitimate interest to understand User experience and improve the Platform.',
                  'User’s consent is our legal basis to the extent that we use Cookies (as defined below in Section XI) to collect Platform Usage Data.',
                ],
                [ProcessingPurposesTableKeys.StoragePeriods]:
                  'We delete your data upon request or anonymize it upon deletion of your account',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]:
                  'Creating aggregated statistics for client to provide our clients with insights on their usage of the Platform.',
                [ProcessingPurposesTableKeys.DataCategories]: 'Platform Usage Data ',
                [ProcessingPurposesTableKeys.LegalBasis]: [
                  'Our legitimate interest to understand the use of the Platform or if specifically requested by our clients, the legal basis is the performance of the contract with our clients.',
                  'User’s consent is our legal basis to the extent that we use Cookies (as defined below in Section XI) to collect Platform Usage Data.',
                ],
                [ProcessingPurposesTableKeys.StoragePeriods]: 'We delete your personal data upon request',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]: [
                  'We use Appcues to notify relevant Users on updates and disruptions on the Platform. As well as to better understand Users’ experience and needs to optimise our service and communication about capabilities.',
                  'Appcues also enables us to make your Platform experience more interactive by implementing features including User guides, dialogues and hotspots. Some elements may be configured to appear for Users meeting certain criteria (e.g. has visited the Platform at least 2 times), however they are never configured to target individual Users.',
                ],
                [ProcessingPurposesTableKeys.DataCategories]: 'User Account Data, Platform Usage Data (B)',
                [ProcessingPurposesTableKeys.LegalBasis]: [
                  'Performance of our contract with clients for the provision of the Platform or our legitimate interest to optimize the users’ experience on the Platform.',
                  'If you do not want to receive these targeted messages you can turn of the DNT (Do-not-track) option in your browser. Please be aware that you then might miss important messaging to you.',
                ],
                [ProcessingPurposesTableKeys.StoragePeriods]: 'We delete your personal data upon request',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]: [
                  'Creating tickets and enabling feedback to provide technical and specialist support via our support ticketing system and processing feedback that Users give us in connection with their experience of the Platform.',
                  'This may involve the collation of the personal data with information (potentially including other personal data) about other interactions the User has had with us. These interactions provide us with a background (e.g. about our prior technical support or your prior requests) which assists us to tailor our support and to enhance the overall User experience on the Platform.',
                ],
                [ProcessingPurposesTableKeys.DataCategories]: [
                  'Ticketing: User Account Data, user email, user’s name, country and other personal data if and to the extent the User’s write a problem description or question or include optional attachments that may include other personal data.',
                  'Feedback: User Account Data, other personal data if and to the extent the User’s write a feedback message or include optional attachments that may include other personal data.',
                ],
                [ProcessingPurposesTableKeys.LegalBasis]:
                  'Our legitimate interest to respond to your requests and process you feedback to optimize the Platform.',
                [ProcessingPurposesTableKeys.StoragePeriods]: 'We delete your personal data upon request',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]: [
                  'Analysing the adoption (frequency of use) of the Platform in order to drive product adoption, support Users in gaining the maximum benefit from the Platform and enable us to identify Users who may require assistance in using the Platform.',
                  'We export and combine the data using Microsoft Power BI. Actions taken on the basis of Power BI analyses may include contacting a User via email to offer Platform support or provide information about the Platform; or sending marketing communications to Users.',
                ],
                [ProcessingPurposesTableKeys.DataCategories]:
                  'User Account Data, Access Log Data, Platform Usage Data (A), Prospective Account Data, Additionally, user email, user’s name, country',
                [ProcessingPurposesTableKeys.LegalBasis]: [
                  'Our legitimate interest to understand User experience whereas User’s consent is our legal basis',
                  [
                    'for any marketing communication we send to User.',
                    'to the extent that we use Cookies (as defined below in Section XI) to collect Platform Usage Data.',
                  ],
                ],
                [ProcessingPurposesTableKeys.StoragePeriods]: 'We delete your personal data upon request',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]:
                  'Storing invitation details, for the purpose of providing better support onboarding Prospective Users to the Platform to become Platform Users if a user invitation was sent by existing Platform Users.',
                [ProcessingPurposesTableKeys.DataCategories]: 'User Account Data',
                [ProcessingPurposesTableKeys.LegalBasis]:
                  'Our legitimate interest to process Platform invitations initiated by existing Users.',
                [ProcessingPurposesTableKeys.StoragePeriods]:
                  'This details about is stored for the time the invitations are valid. We delete your data upon request or anonymize it upon deletion of your account.',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]:
                  'Sending transactional emails to Users to update them about new activities on their User account (e.g. new uploaded data sets, received messages from other Users).',
                [ProcessingPurposesTableKeys.DataCategories]: 'User Account Data, Platform Usage Data (D)',
                [ProcessingPurposesTableKeys.LegalBasis]:
                  'As applicable: Performance of our contract with clients for the provision of the Platform and our legitimate interests to provide the Platform to Employees.',
                [ProcessingPurposesTableKeys.StoragePeriods]: '30 days.',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]: [
                  'Presenting relevant upsell offers to users based on their current subscription.',
                  'Where users have interacted with the offer (e.g. request a free trial, request a follow up) we store and process the request.',
                  'We analyze the data to improve finetuning our upsell campaigns.',
                ],
                [ProcessingPurposesTableKeys.DataCategories]: [
                  'User Account Data',
                  'Interaction: User Account Data, other personal data if users register interest to be contacted about additional products, data or solutions.',
                ],
                [ProcessingPurposesTableKeys.LegalBasis]: [
                  'Our legitimate interest to process',
                  [
                    'account data to show relevant upsell offers and/or limit the number of upsell offers shown to user.',
                    'your requests about being contacted or to stop displaying a campaign.',
                    'The performance of upsell campaigns.',
                  ],
                ],
                [ProcessingPurposesTableKeys.StoragePeriods]:
                  'We delete your data upon request or anonymize it upon deletion of your account.',
              },
              {
                [ProcessingPurposesTableKeys.ProcessingPurposes]:
                  'Process user account data for the purpose of showing relevant feedback forums; for publishing feedback that you provide in our Platform discussion forum ("User Voice") together your name. We use such feedback with other user data for product improvement and reporting purposes. ',
                [ProcessingPurposesTableKeys.DataCategories]:
                  'User Account Data, User ideas, feedback, feature requests, comments',
                [ProcessingPurposesTableKeys.LegalBasis]: [
                  'Our legitimate interest to process your feedback to improve our Platform and for internal reporting purposes as well as making it available to other Platform users for discussion.',
                  'User’s consent is our legal basis if we contact users in case we require clarification for any ideas submitted.',
                ],
                [ProcessingPurposesTableKeys.StoragePeriods]:
                  'We delete feedback upon account deletion or, to the extent that others have supported your feedback, anonymize it.',
              },
            ]}
          />
        </PolicyPage.Chapter>
        <PolicyPage.Chapter
          title="Publicly available sources, source from which the data originate"
          id="Publicly available sources"
        >
          <p>
            In certain circumstances, we may also collect personal data about you from sources other than yourself. We
            process personal data that we have permissibly obtained from publicly accessible sources and are allowed to
            process. Such sources include e.g. professional social networks such as LinkedIn and XING, but also your
            respective company websites and, where applicable, other websites.
          </p>
        </PolicyPage.Chapter>
        <PolicyPage.Chapter title="Recipients" id="recipients">
          <p>
            We may share your personal data with the Group. Within Group, only employees and departments with a "need to
            know" have access to your personal data and only to the extent necessary. Regarding the transfer of your
            personal data within the Group, the companies of the Group are either independent controllers, joint
            controllers or processors, depending on the processing activity.
          </p>
          <p>
            NIQ transfers your personal data to the following third-party recipients within the European Economic Area
            who process the data as our processors:
          </p>
          <ul className="pl-4">
            <li className="text-sm">Hotjar Limited, located in Malta</li>
            <li className="text-sm">Qualtrics, LLC with servers located in Germany and Ireland</li>
          </ul>
          <p>
            Please note that the following transfers to third-party recipients who process data as our processors
            outside the European Economic Area take place:
          </p>
          <ul className="pl-4">
            {[
              'Amplitude, Inc. located in the United States of America',
              'Appcues Inc. located in the United States of America',
              'Auth0, Inc. located in the United States of America',
              'HubSpot, Inc. located in the United States of America',
              'Microsoft Corporation located in the United States of America',
              'salesforce.com, inc. located in the United States of America',
              'Segment.io, Inc. located in the United States of America',
              'Twilio Inc. located in the United States of America',
            ].map(item => (
              <li key={item} className="text-sm">
                {item}
              </li>
            ))}
          </ul>
          <p>
            Further to the above, please note that in certain situations we may be required by law to disclose your
            personal data to public bodies. For example, this may include responding to requests from courts, law
            enforcement agencies, regulatory agencies or other public/government authorities, which may include public
            bodies located outside your country of residence.
          </p>
        </PolicyPage.Chapter>
        <PolicyPage.Chapter title="Transfers of Data outside the EU/EEA" id="Transfers of Data outside the EU/EEA">
          <p>
            Due to the international nature of our business, it may be necessary for us to transfer your personal data
            to other companies within the Group and to third parties outside the jurisdiction in which you reside. For
            this reason, we may transfer your personal data to countries may not have the level of data protection that
            you enjoy under your jurisdiction ("Third Countries") which can, for example, result in impeded enforcement
            of data subjects’ rights, a lack of control over further processing and access by state authorities.
          </p>
          <p>
            Within the Group, we have concluded an intra-group data transfer agreement with the relevant transfer
            mechanisms (for example, standard contractual clauses of the European Commission) and additional safeguards,
            if necessary, to ensure an adequate level of protection for your personal data when it is transferred to
            Third Countries.
          </p>
          <p>
            Insofar as we transfer your personal data to other recipients in Third Countries, we ensure that your data
            is protected by adequate safeguards recognized by your jurisdiction (for example, by concluding standard
            contractual clauses of the European Commission or by means of binding corporate rules of our business
            partners) and supplementary measures (if necessary).
          </p>
          <p>Please use the contact details set out below to obtain a copy of the safeguards.</p>
        </PolicyPage.Chapter>
        <PolicyPage.Chapter title="Are you obliged to provide your personal data?" id="obligation-to-provide-data">
          <p>
            In principle, you are not obliged to provide your personal data. However, if you do not provide your
            personal data, we may only be able to provide you with limited services or not answer your enquiries. If the
            processing of your personal data is necessary for the fulfilment of a contract between you and us and you do
            not provide the required information, we may discontinue our contractual services. In this case, we will
            notify you in advance.
          </p>
        </PolicyPage.Chapter>
        <PolicyPage.Chapter title="Your data subject rights" id="Your data subject rights">
          <p>
            You may have certain rights in relation to your personal data. For example, if the processing of your
            personal data is subject to the GDPR, you have the following rights to the extent provided applicable law:
          </p>
          <ul className="pl-4">
            <li className="text-sm">
              right to <span className="font-bold">access</span> and right to{' '}
              <span className="font-bold">receive a copy of your personal data</span>,
            </li>
            <li className="text-sm">
              right to <span className="font-bold">rectification</span>,
            </li>
            <li className="text-sm">
              right to <span className="font-bold">erasure</span>,
            </li>
            <li className="text-sm">
              right to <span className="font-bold">restriction of processing</span>,
            </li>
            <li className="text-sm">
              right to <span className="font-bold">data portability</span>,
            </li>
            <li className="text-sm">
              right to <span className="font-bold">withdraw your consent</span>: You can withdraw consent at any time
              with effect for the future by contacting us at{' '}
              <Link href="mailto:connect.privacy@smb.nielseniq.com">connect.privacy@smb.nielseniq.com</Link> or using
              the contact information in section 1.
            </li>
            <li className="text-sm">
              right to <span className="font-bold">lodge a complaint</span>: In the event of a (suspected) infringement
              of applicable data protection laws, you may lodge a complaint with a supervisory authority.
            </li>
            <p className="text-sm pt-2">
              We do not make decisions based solely on automated processing, including profiling, which produces legal
              effects or similarly significantly affects you.
            </p>
            <p className="border border-default rounded-none pl-2 text-sm">
              To the extent provided for by applicable law, you have a general{' '}
              <span className="font-bold">right to object</span>, on grounds relating to your situation, if we process
              your personal data based on our legitimate interest. This means that you must always give reasons for your
              objection and the reasons for the objection must not result from the processing situation as such but must
              be justified in your person. We will no longer process your personal data unless we can demonstrate
              compelling legitimate grounds for the processing which override your interests, rights and freedoms or for
              the establishment, exercise or defence of legal claims. Further, you have the right to object to the
              processing of your personal data for direct marketing purposes at any time.
            </p>
          </ul>
        </PolicyPage.Chapter>
        <PolicyPage.Chapter title="Duration of the processing" id="duration-of-processing">
          <p>
            We will only process your personal data for as long as is necessary to achieve the above purposes. For
            details, please see column "data storage periods or rules" in sec. 2. Third parties engaged by us will store
            your personal data on their systems for as long as is necessary in connection with the provision of services
            to us in accordance with the relevant contract. We will delete or anonymise your personal data as soon as it
            is no longer required for the purposes described in this privacy notice and if we have no legal basis to
            further store your personal data.
          </p>
          <p>
            In addition, the retention period may be extended if we are subject to statutory retention and documentation
            obligations. The retention period may also be based on the statutory limitation periods. In certain
            circumstances, we may also need to store your personal data for longer, e.g. in connection with authority or
            legal proceedings.{' '}
          </p>
          <p>Regarding the use and retention period of cookies, please note section XII.</p>
        </PolicyPage.Chapter>
        <PolicyPage.Chapter title="Security" id="security">
          <p>
            We protect your personal data from loss, misuse, disclosure, alteration, unavailability, unauthorised access
            and destruction and maintain the confidentiality of your personal data. This is also ensured using
            appropriate technical and organisational measures. We choose our security measures considering the state of
            the art, the costs of implementation and the nature, scope, context and purposes of processing as well as
            the risk of varying likelihood and severity for the rights and freedoms of natural persons and continuously
            improve them. Technical measures include, for example, the use of encryption (e.g. TSL encryption for
            personal data in transit), access control to our systems, monitoring of system resources and system
            messages, ensuring the availability and resilience of systems and services.
          </p>
          <p>
            Organizational measures include, for example, defining roles and responsibilities, ensuring the correct and
            secure operation of information processing systems, regular training and awareness-raising of employees, as
            well as evaluating and assessing the effectiveness of the aforementioned measures. Access to your personal
            data is only granted to employees, service providers or Group companies who require such access for the
            fulfilment of a business purpose or for the performance of their duties.
          </p>
        </PolicyPage.Chapter>
        <PolicyPage.Chapter title="Cookies and other technologies " id="cookies-and-other-technologies">
          <p>
            Our website contains cookies and other technologies (e.g. pixels, scripts) (together "Cookies"). Cookies are
            used to make our website user-friendly, effective and secure. Cookies are, for example, small text files
            that are stored on your terminal device and contain personal data such as personal settings and login
            information.
          </p>
          <p>We use the following categories of Cookies: </p>
          <ul className="pl-4">
            <li className="text-sm">
              <span className="font-bold">Performance Cookies: </span> These Cookies allow us to track visits and
              website usage so that we can measure and improve the performance of our website. They help us understand
              which pages are the most or least popular, or to record error messages on the website. All analysis based
              on this information is aggregated.
            </li>
            <li className="text-sm">
              <span className="font-bold">Functional Cookies: </span> These Cookies allow us to improve the
              functionality and personalization of the website. They ensure that your website preferences (e.g.,
              settings or filters) are maintained and help us to include third-party services on the website. Any
              analysis performed based on this information is aggregated.
            </li>
            <li className="text-sm">
              <span className="font-bold">Advertising Cookies: </span> These Cookies help us to better analyze the
              impact of our website and your interests, e.g. to show you personalized advertising or put other content
              on our or other websites. These may be displayed on our website or on third-party websites. In particular,
              the Cookie collects information about your browsing activities to understand which topics are relevant to
              you.
            </li>
            <li className="text-sm">
              <span className="font-bold">Strictly Necessary Cookies: </span> These Cookies are necessary for the
              functioning and management of the website and cannot be disabled in our systems. They are usually set
              based on your input, such as when you set your Cookie preferences, log in, or fill out forms. You can set
              your browser to block these cookies, but then some parts of the website will not work.
            </li>
          </ul>
          <p>
            We use first- and third-party Cookies. First party Cookies come from our platform and send information only
            to us; third party Cookies are placed on our website by third parties and send information about your device
            to other companies that recognise the Cookie. We use session Cookies, which are only stored for individual
            online sessions and are deleted when you close your browser; and persistent Cookies, which are deleted when
            they reach their expiry date or are deleted by the user.
          </p>
          <p>
            We place Strictly Necessary Cookies to provide you with a tele media service or other equivalent information
            society service expressly requested by you. The subsequent processing of Strictly Necessary Cookies is based
            on our legitimate interest to provide you with a technically optimized, user-friendly and appropriate
            website or your consent (as applicable). We use other Cookies only with your consent. Where we rely on
            consent, you can withdraw your consent at any time with effect for the future, e.g. by{' '}
            <Link onClick={openCookieSettings}>changing your cookie preferences.</Link>
          </p>
          <p>
            Please note that Microsoft cookies are only set when using the "Log in with your NIQ account" option i.e.
            are only set on devices of Employees for authentication purposes.
          </p>
          <p>A list of Cookies used by our website can be found in the following:</p>
          <Table
            columnConfig={[
              {
                columnKey: CookiesTableKeys.Category,
                columnName: 'Cookie category',
                cellConfig: { ...tableCellDefaults, maxWidth: 100 },
                subComponent: TableSubComponent,
              },
              {
                columnKey: CookiesTableKeys.SubGroup,
                columnName: 'Cookie subgroup',
                cellConfig: { ...tableCellDefaults, maxWidth: 90 },
                subComponent: TableSubComponent,
              },
              {
                columnKey: CookiesTableKeys.Name,
                columnName: 'Cookie name',
                cellConfig: { ...tableCellDefaults, maxWidth: 190 },
                subComponent: TableSubComponent,
              },
              {
                columnKey: CookiesTableKeys.FirstOrThird,
                columnName: 'First / third party',
                cellConfig: { ...tableCellDefaults, maxWidth: 50 },
                subComponent: TableSubComponent,
              },
              {
                columnKey: CookiesTableKeys.LifeTime,
                columnName: 'Cookie lifetime',
                cellConfig: { ...tableCellDefaults, maxWidth: 90 },
                subComponent: TableSubComponent,
              },
              {
                columnKey: CookiesTableKeys.Domain,
                columnName: 'Cookie domain',
                cellConfig: { ...tableCellDefaults, maxWidth: 90 },
                subComponent: TableSubComponent,
              },
            ]}
            data={[
              {
                [CookiesTableKeys.Category]: 'Strictly Necessary',
                [CookiesTableKeys.SubGroup]: 'login.microsoftonline.com',
                [CookiesTableKeys.Name]: 'SignInStateCookie, x-ms-gateway-slice, x-ms-RefreshTokenCredential',
                [CookiesTableKeys.FirstOrThird]: 'First Party',
                [CookiesTableKeys.LifeTime]: 'Session',
                [CookiesTableKeys.Domain]: 'login.microsoftonline.com',
              },
              {
                [CookiesTableKeys.Category]: 'Strictly Necessary',
                [CookiesTableKeys.SubGroup]: 'login.microsoftonline.com',
                [CookiesTableKeys.Name]:
                  'buid, CCState, ch, ESTSAUTH, ESTSAUTHLIGHT, ESTSAUTHPERSISTENT, ESTSSC, fpc, stsservicecookie',
                [CookiesTableKeys.FirstOrThird]: 'First Party',
                [CookiesTableKeys.LifeTime]:
                  'Session, 3 Days, 90 Days, Session, Session, 90 Days, Session, 30 Days, Session',
                [CookiesTableKeys.Domain]: 'login.microsoftonline.com',
              },
              {
                [CookiesTableKeys.Category]: 'Strictly Necessary',
                [CookiesTableKeys.SubGroup]: 'platform.gfk.com',
                [CookiesTableKeys.Name]:
                  '_legacy_auth0.is.authenticated, auth0.is.authenticated, OptanonAlertBox-Closed, OptanonConsent',
                [CookiesTableKeys.FirstOrThird]: 'First Party',
                [CookiesTableKeys.LifeTime]: 'A few seconds, A few seconds, 364 Days, 364 Days',
                [CookiesTableKeys.Domain]: 'platform.gfk.com',
              },
              {
                [CookiesTableKeys.Category]: 'Strictly Necessary',
                [CookiesTableKeys.SubGroup]: 'www.gfk.com',
                [CookiesTableKeys.Name]: '__cfruid',
                [CookiesTableKeys.FirstOrThird]: 'Third Party',
                [CookiesTableKeys.LifeTime]: 'Session',
                [CookiesTableKeys.Domain]: 'www.gfk.com',
              },
              {
                [CookiesTableKeys.Category]: 'Strictly Necessary',
                [CookiesTableKeys.SubGroup]: 'siteintercept.qualtrics.com',
                [CookiesTableKeys.Name]: 'QSI_SI_5Ayex0lS5Zjr3sW_intercept, QSI_SI_b3YoNh4TOiMFDF4_intercept',
                [CookiesTableKeys.FirstOrThird]: 'Third Party',
                [CookiesTableKeys.LifeTime]: 'A few seconds, A few seconds',
                [CookiesTableKeys.Domain]: 'siteintercept.qualtrics.com',
              },
              {
                [CookiesTableKeys.Category]: 'Strictly Necessary',
                [CookiesTableKeys.SubGroup]: 'cdn.auth0.com',
                [CookiesTableKeys.Name]: 'com.auth0.auth',
                [CookiesTableKeys.FirstOrThird]: 'Third Party',
                [CookiesTableKeys.LifeTime]: 'A few seconds',
                [CookiesTableKeys.Domain]: 'cdn.auth0.com',
              },
              {
                [CookiesTableKeys.Category]: 'Strictly Necessary',
                [CookiesTableKeys.SubGroup]: 'auth.gfk.com',
                [CookiesTableKeys.Name]: '__cf_bm, _csrf, auth0, auth0_compat, did, did_compat',
                [CookiesTableKeys.FirstOrThird]: 'Third Party',
                [CookiesTableKeys.LifeTime]: 'A few seconds, 10 Days, 1 Day, 1 Day, 364 Days, 364 Days',
                [CookiesTableKeys.Domain]: 'auth.gfk.com',
              },
              {
                [CookiesTableKeys.Category]: 'Strictly Necessary',
                [CookiesTableKeys.SubGroup]: 'api.gfk.konzepthaus-ws.com',
                [CookiesTableKeys.Name]: 'ARRAffinity, ARRAffinitySameSite',
                [CookiesTableKeys.FirstOrThird]: 'Third Party',
                [CookiesTableKeys.LifeTime]: 'Session, Session',
                [CookiesTableKeys.Domain]: 'api.gfk.konzepthaus-ws.com',
              },
              {
                [CookiesTableKeys.Category]: 'Performance',
                [CookiesTableKeys.SubGroup]: 'gfk.com',
                [CookiesTableKeys.Name]:
                  '__tld__, ajs_anonymous_id, ajs_group_id, ajs_user_id, amplitude_id_*, amplitude_idundefinedgfk.com, amplitude_testgfk.com, apt.sid, apt.temp-*, apt.uid',
                [CookiesTableKeys.FirstOrThird]: 'First Party',
                [CookiesTableKeys.LifeTime]:
                  'Session, 365 Days, A few seconds, A few seconds, 3649 Days, Session, Session, A few seconds, A few seconds, 364 Days',
                [CookiesTableKeys.Domain]: 'gfk.com',
              },
              {
                [CookiesTableKeys.Category]: 'Performance',
                [CookiesTableKeys.SubGroup]: 'platform.gfk.com',
                [CookiesTableKeys.Name]: '__hssc, __hssrc, __hstc, amplitude_cookie_test, hubspotutk',
                [CookiesTableKeys.FirstOrThird]: 'First Party',
                [CookiesTableKeys.LifeTime]: 'A few seconds, Session, 389 Days, Session, 389 Days',
                [CookiesTableKeys.Domain]: 'platform.gfk.com',
              },
              {
                [CookiesTableKeys.Category]: 'Performance',
                [CookiesTableKeys.SubGroup]: 'hubspot.com',
                [CookiesTableKeys.Name]: '__cf_bm',
                [CookiesTableKeys.FirstOrThird]: 'Third Party',
                [CookiesTableKeys.LifeTime]: 'A few seconds',
                [CookiesTableKeys.Domain]: 'hubspot.com',
              },
              {
                [CookiesTableKeys.Category]: 'Performance',
                [CookiesTableKeys.SubGroup]: 'js.hs-analytics.net',
                [CookiesTableKeys.Name]: '__hssc, __hssrc, __hstc, hubspotutk',
                [CookiesTableKeys.FirstOrThird]: 'Third Party',
                [CookiesTableKeys.LifeTime]: 'A few seconds, Session, 389 Days, 389 Days',
                [CookiesTableKeys.Domain]: 'js.hs-analytics.net',
              },
              {
                [CookiesTableKeys.Category]: 'Performance',
                [CookiesTableKeys.SubGroup]: 'js.hs-banner.com',
                [CookiesTableKeys.Name]: '__hssc, __hssrc, __hstc, hubspotutk',
                [CookiesTableKeys.FirstOrThird]: 'Third Party',
                [CookiesTableKeys.LifeTime]: 'A few seconds, Session, 179 Days, 179 Days',
                [CookiesTableKeys.Domain]: 'js.hs-banner.com',
              },
              {
                [CookiesTableKeys.Category]: 'Performance',
                [CookiesTableKeys.SubGroup]: '_hjAbsoluteSessionInProgress',
                [CookiesTableKeys.Name]: 'gfk.com',
                [CookiesTableKeys.FirstOrThird]: 'Hotjar',
                [CookiesTableKeys.LifeTime]: '30 minutes',
                [CookiesTableKeys.Domain]: '_hjAbsoluteSessionInProgress',
              },
              {
                [CookiesTableKeys.Category]: 'Performance',
                [CookiesTableKeys.SubGroup]: '_hjFirstSeen',
                [CookiesTableKeys.Name]: 'gfk.com',
                [CookiesTableKeys.FirstOrThird]: 'Hotjar',
                [CookiesTableKeys.LifeTime]: 'Session',
                [CookiesTableKeys.Domain]: '_hjFirstSeen',
              },
            ]}
          />
          <p className="pt-2">
            We also use third-party technologies as explained under Section IV. You can also use our website without
            Cookies, but you might not be able to use our website to its full extent or to use certain functionalities.
          </p>
        </PolicyPage.Chapter>
        <PolicyPage.Chapter title="Questions, exercising your data protection rights, complaints" id="questions">
          <p>
            If you have any questions or complaints about the collection, use or retention of your personal data, or if
            you wish to exercise any of your rights in relation to your personal data, you can contact our data
            protection officer by emailing{' '}
            <Link href="mailto: connect.privacy@smb.nielseniq.com"> connect.privacy@smb.nielseniq.com</Link>.
          </p>
          <p>
            We will investigate and attempt to remedy any complaint or dispute regarding the processing of your personal
            data. You can also lodge a complaint with the competent data protection authority.
          </p>
        </PolicyPage.Chapter>
      </PolicyPage.Chapters>
    </PolicyPage.Content>
  );
}

export { PrivacyPolicy, PrivacyPolicyCopy };
